import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPaymentStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackPurchase } from "@/lib/tracking";

export default function PagamentoEsito() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const canceled = params.get("canceled");
  const [st, setSt] = useState(null);
  const [tries, setTries] = useState(0);
  const timer = useRef(null);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sid) return;
    let stop = false;
    const poll = async () => {
      try {
        const r = await getPaymentStatus(sid);
        if (stop) return;
        setSt(r);
        if (r.status === "paid" && r.fulfilled) { trackPurchase(r); refresh?.(); return; }
        if (["failed", "expired", "refunded"].includes(r.status)) return;
      } catch { /* keep polling */ }
      setTries((t) => t + 1);
      timer.current = setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; clearTimeout(timer.current); };
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = sid && (!st || st.status === "initiated");
  const paid = st?.status === "paid" && st?.fulfilled;
  const failed = canceled || ["failed", "expired", "refunded"].includes(st?.status);

  return (
    <div className="min-h-screen px-4 py-16 flex items-start justify-center" data-testid="payment-result-page">
      <div className="w-full max-w-md nadia-card p-8 text-center space-y-4">
        {canceled ? <XCircle size={40} className="mx-auto text-amber-300" /> : paid ? <CheckCircle2 size={40} className="mx-auto text-emerald-300" /> : failed ? <XCircle size={40} className="mx-auto text-rose-300" /> : tries > 24 ? <Clock size={40} className="mx-auto text-amber-300" /> : <Loader2 size={40} className="mx-auto text-cyan-300 animate-spin" />}
        <h1 className="font-heading text-2xl font-semibold text-white" data-testid="payment-result-title">
          {canceled ? "Pagamento annullato" : paid ? "Pagamento confermato" : failed ? "Pagamento non riuscito" : tries > 24 ? "Pagamento in elaborazione" : "Sto verificando il pagamento…"}
        </h1>
        <p className="text-sm text-slate-400" data-testid="payment-result-text">
          {canceled && "Nessun addebito effettuato. Puoi riprovare quando vuoi."}
          {paid && `${st.label}: ${st.kind === "subscription" ? "piano attivo e crediti accreditati" : "crediti aggiunti al tuo wallet"}. Ti ho inviato la conferma via email; la fattura arriverà nella tua area personale.`}
          {!canceled && failed && "Stripe non ha confermato il pagamento. Nessun credito è stato addebitato: riprova o usa un altro metodo."}
          {pending && tries <= 24 && "Attendo la conferma sicura da Stripe: bastano pochi secondi."}
          {pending && tries > 24 && "La banca sta ancora elaborando il pagamento. Appena Stripe conferma, attivo tutto automaticamente e ti avviso via email."}
        </p>
        {st && <div className="text-[11px] text-slate-600" data-testid="payment-session">Riferimento: {st.session_id}{st.stripe_mode === "shared_test" ? " · ambiente di test" : ""}</div>}
        <div className="flex gap-2 justify-center pt-2">
          <Button asChild className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold" data-testid="payment-go-app"><Link to="/app">Vai alla dashboard</Link></Button>
          {(failed || canceled) && <Button asChild variant="outline" className="border-white/10" data-testid="payment-retry"><Link to="/piani">Torna ai piani</Link></Button>}
        </div>
        <div className="text-[11px] text-slate-600 pt-2">Nadia Cloud · support@nadiacloud.it</div>
      </div>
    </div>
  );
}
